import React, { useState, useEffect, useMemo } from 'react';
import UrlConfig from '../../data/UrlConfig'; // Ajusta la ruta si es necesario

const ProductManagement = () => {
  // --- Estados para la Gestión de Productos ---
  const [products, setProducts] = useState([]);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productFormState, setProductFormState] = useState({
    PathUrl: '',
    Code: '',
    Title: '',
    Description: '',
    Type: '', // ¡NUEVO CAMPO!
  });

  // --- Estados para la Gestión de Ítems de Producto ---
  const [selectedProductForItems, setSelectedProductForItems] = useState(null); // Producto cuyos ítems estamos gestionando
  const [productItems, setProductItems] = useState([]); // Ítems del producto seleccionado
  const [editingProductItem, setEditingProductItem] = useState(null);
  const [productItemFormState, setProductItemFormState] = useState({
    PathUrl: '',
    Code: '',
    Title: '',
    Description: '',
    VideoUrl: '',
    PdfUrl: '',
    ImageUrl: '',
    DocUrl: '',
  });

  // --- Estados para la Gestión de Usuarios y Asignaciones ---
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null); // Usuario seleccionado para gestionar asignaciones
  const [userAssignedProducts, setUserAssignedProducts] = useState([]); // Productos asignados al usuario
  const [userAssignedProductItems, setUserAssignedProductItems] = useState([]); // Ítems asignados al usuario

  // --- Estados para Mensajes y Carga Global ---
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // --- Función Genérica para Llamar a la API ---
  const callApi = async (_action, data = {}) => {
    setIsLoading(true);
    setError('');
    setMessage('');
    try {
      const response = await fetch(UrlConfig.products_management, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ _action, ...data }),
      });

      const responseData = await response.json();

      if (response.ok && (responseData.status === 'success' || responseData.status === 'info')) {
        setMessage(responseData.message);
        return responseData;
      } else {
        const errorMessage = responseData.message || 'Ocurrió un error inesperado.';
        throw new Error(errorMessage);
      }
    } catch (err) {
      setError(err.message);
      // Re-lanza el error para que las funciones que llaman a `callApi` puedan manejarlo específicamente si es necesario.
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  // --- Efecto Inicial para Cargar Datos ---
  useEffect(() => {
    fetchProducts();
    fetchUsers();
  }, []);

  // --- Funciones de Gestión de Productos ---
  const fetchProducts = async () => {
    try {
      const data = await callApi('getAllProducts');
      setProducts(data?.data || []);
    } catch (err) { /* Error ya manejado */ }
  };

  const handleProductFormChange = (e) => {
    const { name, value } = e.target;
    setProductFormState((prevState) => ({ ...prevState, [name]: value }));
  };

  const handleProductFormSubmit = async (e) => {
    e.preventDefault();
    const action = editingProduct ? 'updateProduct' : 'createProduct';
    // productFormState ya contiene 'Type' debido a la inicialización y handleProductFormChange
    const data = editingProduct ? { ...productFormState, Id: editingProduct.Id } : productFormState;
    try {
      await callApi(action, data);
      setProductFormState({ PathUrl: '', Code: '', Title: '', Description: '', Type: '' }); // Limpiar también Type
      setEditingProduct(null);
      fetchProducts();
    } catch (err) { /* Error ya manejado */ }
  };

  const handleEditProductClick = (product) => {
    setEditingProduct(product);
    setProductFormState({
      ...product,
      Description: product.Description || '',
      Type: product.Type || '', // Asegura que el campo Type se cargue para edición
    });
    setMessage(''); setError('');
    if (selectedProductForItems?.Id === product.Id) setSelectedProductForItems(null); // Cierra la vista de ítems si era este producto
  };

  const handleDeleteProductClick = async (productId) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar este producto? Esto también eliminará todos sus ítems y asignaciones a usuarios.')) return;
    try {
      await callApi('deleteProduct', { Id: productId });
      fetchProducts();
      // Limpiar estados relacionados si el producto eliminado estaba en uso
      if (selectedProductForItems?.Id === productId) setSelectedProductForItems(null);
      if (selectedUser) {
        fetchUserAssignedProducts(selectedUser.Id);
        fetchUserAssignedProductItems(selectedUser.Id);
      }
      setMessage('Producto eliminado exitosamente y sus asignaciones relacionadas.');
    } catch (err) { /* Error ya manejado */ }
  };

  const handleCancelEditProduct = () => {
    setEditingProduct(null);
    setProductFormState({ PathUrl: '', Code: '', Title: '', Description: '', Type: '' }); // Limpiar también Type
    setError(''); setMessage('');
  };

  // --- Funciones de Gestión de Ítems de Producto ---
  const handleManageItemsClick = async (product) => {
    setSelectedProductForItems(product);
    setProductItemFormState({ PathUrl: '', Code: '', Title: '', Description: '', VideoUrl: '', PdfUrl: '', ImageUrl: '', DocUrl: '' });
    setEditingProductItem(null);
    setMessage(''); setError('');
    await fetchProductItems(product.Id);
  };

  const fetchProductItems = async (productId) => {
    try {
      const data = await callApi('getAllProductItemsForProduct', { ProductId: productId });
      setProductItems(data?.data || []);
    } catch (err) { /* Error ya manejado */ }
  };

  const handleProductItemFormChange = (e) => {
    const { name, value } = e.target;
    setProductItemFormState((prevState) => ({ ...prevState, [name]: value }));
  };

  const handleProductItemFormSubmit = async (e) => {
    e.preventDefault();
    if (!selectedProductForItems) { setError('Debes seleccionar un producto.'); return; }
    const action = editingProductItem ? 'updateProductItem' : 'createProductItem';
    const data = editingProductItem
      ? { ...productItemFormState, Id: editingProductItem.Id, ProductId: selectedProductForItems.Id }
      : { ...productItemFormState, ProductId: selectedProductForItems.Id };
    try {
      await callApi(action, data);
      setProductItemFormState({ PathUrl: '', Code: '', Title: '', Description: '', VideoUrl: '', PdfUrl: '', ImageUrl: '', DocUrl: '' });
      setEditingProductItem(null);
      fetchProductItems(selectedProductForItems.Id);
    } catch (err) { /* Error ya manejado */ }
  };

  const handleEditProductItemClick = (item) => {
    setEditingProductItem(item);
    setProductItemFormState({ ...item, Description: item.Description || '' });
    setMessage(''); setError('');
  };

  const handleDeleteProductItemClick = async (itemId) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar este ítem de producto? También se eliminarán las asignaciones a usuarios.')) return;
    try {
      await callApi('deleteProductItem', { Id: itemId });
      if (selectedProductForItems) fetchProductItems(selectedProductForItems.Id);
      if (selectedUser) fetchUserAssignedProductItems(selectedUser.Id); // Refrescar ítems asignados del usuario
      setMessage('Ítem de producto eliminado exitosamente y sus asignaciones relacionadas.');
    } catch (err) { /* Error ya manejado */ }
  };

  const handleCancelEditProductItem = () => {
    setEditingProductItem(null);
    setProductItemFormState({ PathUrl: '', Code: '', Title: '', Description: '', VideoUrl: '', PdfUrl: '', ImageUrl: '', DocUrl: '' });
    setError(''); setMessage('');
  };

  // --- Funciones de Gestión de Usuarios y Asignaciones ---
  const fetchUsers = async () => {
    try {
      const data = await callApi('getAllUsers');
      setUsers(data?.data || []);
    } catch (err) { /* Error ya manejado */ }
  };

  const handleManageUserAssignmentsClick = async (user) => {
    setSelectedUser(user);
    setMessage(''); setError('');
    await fetchUserAssignedProducts(user.Id);
    await fetchUserAssignedProductItems(user.Id);
  };

  const fetchUserAssignedProducts = async (userId) => {
    try {
      const data = await callApi('getAllUserProductsForUser', { UserId: userId });
      setUserAssignedProducts(data?.data || []);
    } catch (err) { /* Error ya manejado */ }
  };

  const fetchUserAssignedProductItems = async (userId) => {
    try {
      const data = await callApi('getAllUserProductItemsForUser', { UserId: userId });
      setUserAssignedProductItems(data?.data || []);
    } catch (err) { /* Error ya manejado */ }
  };

  const handleAssignProductToUser = async (userId, productId) => {
    try {
      await callApi('assignUserProduct', { UserId: userId, ProductId: productId });
      fetchUserAssignedProducts(userId);
    } catch (err) { /* Error ya manejado */ }
  };

  const handleUnassignProductFromUser = async (userProductId, userId) => {
    if (!window.confirm('¿Estás seguro de que quieres desasignar este producto del usuario? Esto también desasignará los ítems de este producto que el usuario tenía asignados.')) return;
    try {
      await callApi('unassignUserProduct', { UserProductId: userProductId, UserId: userId });
      fetchUserAssignedProducts(userId);
      fetchUserAssignedProductItems(userId); // Refrescar también los ítems por si se desasignaron
    } catch (err) { /* Error ya manejado */ }
  };

  const handleAssignProductItemToUser = async (userId, productItemId) => {
    try {
      await callApi('assignUserProductItem', { UserId: userId, ProductItemId: productItemId });
      fetchUserAssignedProductItems(userId);
    } catch (err) { /* Error ya manejado */ }
  };

  const handleUnassignProductItemFromUser = async (userProductItemId, userId) => {
    if (!window.confirm('¿Estás seguro de que quieres desasignar este ítem del usuario?')) return;
    try {
      await callApi('unassignUserProductItem', { UserProductItemId: userProductItemId });
      fetchUserAssignedProductItems(userId);
    } catch (err) { /* Error ya manejado */ }
  };

  // --- Memorización de datos para selectores ---
  const availableProductsForUser = useMemo(() => {
    const assignedProductIds = userAssignedProducts.map(ap => ap.ProductId);
    return products.filter(p => !assignedProductIds.includes(p.Id));
  }, [products, userAssignedProducts]);

  const availableProductItemsForUser = useMemo(() => {
    if (!selectedUser) return [];

    const assignedItemIds = userAssignedProductItems.map(api => api.ProductItemId);
    const assignedProductIds = userAssignedProducts.map(ap => ap.ProductId);

    // Obtener todos los ítems de los productos que *YA ESTÁN ASIGNADOS* al usuario.
    // Esto asegura que solo se puedan asignar ítems de productos que el usuario ya tiene.
    const itemsFromAssignedProducts = productItems.filter(item =>
      assignedProductIds.includes(item.ProductId)
    );

    // Filtrar aquellos ítems que no estén ya asignados al usuario.
    return itemsFromAssignedProducts.filter(item => !assignedItemIds.includes(item.Id));
  }, [selectedUser, userAssignedProducts, userAssignedProductItems, productItems]);


  return (
    <div className="container mx-auto p-4 max-w-6xl">
      <h1 className="text-4xl font-extrabold text-gray-900 mb-8 text-center">Gestión de Productos y Usuarios</h1>

      {/* Mensajes de feedback */}
      {message && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-lg relative mb-4 shadow-sm" role="alert">
          <strong className="font-bold">Éxito!</strong>
          <span className="block sm:inline ml-2">{message}</span>
        </div>
      )}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg relative mb-4 shadow-sm" role="alert">
          <strong className="font-bold">Error!</strong>
          <span className="block sm:inline ml-2">{error}</span>
        </div>
      )}

      {/* --- SECCIÓN DE GESTIÓN DE PRODUCTOS --- */}
      <div className="bg-white shadow-xl rounded-xl p-8 mb-10 border border-gray-200">
        <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center">Gestión de Productos</h2>

        {/* Formulario de Producto */}
        <div className="bg-gray-50 p-6 rounded-lg shadow-inner mb-8 border border-gray-100">
          <h3 className="text-2xl font-bold text-gray-800 mb-4 text-center">
            {editingProduct ? 'Editar Producto Existente' : 'Crear Nuevo Producto'}
          </h3>
          <form onSubmit={handleProductFormSubmit} className="space-y-5">
            <div>
              <label htmlFor="productPathUrl" className="block text-sm font-medium text-gray-700 mb-1">Path URL</label>
              <input type="text" id="productPathUrl" name="PathUrl" value={productFormState.PathUrl} onChange={handleProductFormChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                required disabled={isLoading} />
            </div>
            <div>
              <label htmlFor="productCode" className="block text-sm font-medium text-gray-700 mb-1">Código</label>
              <input type="text" id="productCode" name="Code" value={productFormState.Code} onChange={handleProductFormChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                required disabled={isLoading} />
            </div>
            <div>
              <label htmlFor="productTitle" className="block text-sm font-medium text-gray-700 mb-1">Título</label>
              <input type="text" id="productTitle" name="Title" value={productFormState.Title} onChange={handleProductFormChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                required disabled={isLoading} />
            </div>
            {/* Campo Type agregado */}
            <div>
              <label htmlFor="productType" className="block text-sm font-medium text-gray-700 mb-1">Tipo de Producto</label>
              <input type="text" id="productType" name="Type" value={productFormState.Type} onChange={handleProductFormChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                required disabled={isLoading} />
            </div>
            <div>
              <label htmlFor="productDescription" className="block text-sm font-medium text-gray-700 mb-1">Descripción</label>
              <textarea id="productDescription" name="Description" value={productFormState.Description} onChange={handleProductFormChange}
                rows="3" className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                disabled={isLoading}></textarea>
            </div>
            <div className="flex justify-end space-x-4 mt-6">
              <button type="submit"
                className="inline-flex justify-center py-2 px-6 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                disabled={isLoading}>
                {isLoading ? (editingProduct ? 'Actualizando...' : 'Creando...') : (editingProduct ? 'Actualizar Producto' : 'Crear Producto')}
              </button>
              {editingProduct && (
                <button type="button" onClick={handleCancelEditProduct}
                  className="inline-flex justify-center py-2 px-6 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  disabled={isLoading}>
                  Cancelar Edición
                </button>
              )}
            </div>
          </form>
        </div>

        {/* Lista de Productos */}
        <div className="mt-8">
          <h3 className="text-2xl font-bold text-gray-800 mb-4 text-center">Todos los Productos</h3>
          {isLoading && <p className="text-center text-gray-600">Cargando productos...</p>}
          {!isLoading && products.length === 0 && <p className="text-center text-gray-600">No se encontraron productos.</p>}
          {!isLoading && products.length > 0 && (
            <ul className="divide-y divide-gray-200">
              {products.map((product) => (
                <li key={product.Id} className="py-4 flex flex-col md:flex-row justify-between items-start md:items-center">
                  <div className="flex-1 mb-3 md:mb-0 md:mr-6">
                    <h4 className="text-lg font-semibold text-gray-900">{product.Title} (<span className="text-blue-600">{product.Code}</span>)</h4>
                    <p className="text-sm text-gray-600 break-words">{product.PathUrl}</p>
                    <p className="mt-1 text-sm text-gray-700">Tipo: <span className="font-medium">{product.Type}</span></p> {/* Mostrar el Tipo */}
                    {product.Description && <p className="mt-1 text-sm text-gray-500">{product.Description}</p>}
                  </div>
                  <div className="flex flex-wrap gap-2 justify-start md:justify-end">
                    <button onClick={() => handleEditProductClick(product)}
                      className="px-4 py-2 bg-yellow-500 text-white text-sm font-medium rounded-md shadow-sm hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 disabled:opacity-50"
                      disabled={isLoading}>
                      Editar
                    </button>
                    <button onClick={() => handleDeleteProductClick(product.Id)}
                      className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                      disabled={isLoading}>
                      Eliminar
                    </button>
                    <button onClick={() => handleManageItemsClick(product)}
                      className="px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-md shadow-sm hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50"
                      disabled={isLoading}>
                      Gestionar Ítems
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Sección de Gestión de Ítems de Producto (condicional) */}
      {selectedProductForItems && (
        <div className="bg-white shadow-xl rounded-xl p-8 mb-10 border border-gray-200">
          <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center">
            Ítems para: <span className="text-blue-600">{selectedProductForItems.Title}</span>
          </h2>
          <div className="flex justify-center mb-6">
            <button onClick={() => {
                setSelectedProductForItems(null); setProductItems([]); setEditingProductItem(null);
                setProductItemFormState({ PathUrl: '', Code: '', Title: '', Description: '', VideoUrl: '', PdfUrl: '', ImageUrl: '', DocUrl: '' });
                setMessage(''); setError('');
              }}
              className="inline-flex justify-center py-2 px-6 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-gray-600 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50"
              disabled={isLoading}>
              Cerrar Gestión de Ítems
            </button>
          </div>

          {/* Formulario de Ítem de Producto */}
          <div className="bg-gray-50 p-6 rounded-lg shadow-inner mb-8 border border-gray-100">
            <h3 className="text-2xl font-bold text-gray-800 mb-4 text-center">
              {editingProductItem ? 'Editar Ítem de Producto' : 'Crear Nuevo Ítem de Producto'}
            </h3>
            <form onSubmit={handleProductItemFormSubmit} className="space-y-5">
              <div>
                <label htmlFor="itemPathUrl" className="block text-sm font-medium text-gray-700 mb-1">Path URL</label>
                <input type="text" id="itemPathUrl" name="PathUrl" value={productItemFormState.PathUrl} onChange={handleProductItemFormChange}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  disabled={isLoading} />
              </div>
              <div>
                <label htmlFor="itemCode" className="block text-sm font-medium text-gray-700 mb-1">Código</label>
                <input type="text" id="itemCode" name="Code" value={productItemFormState.Code} onChange={handleProductItemFormChange}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  required disabled={isLoading} />
              </div>
              <div>
                <label htmlFor="itemTitle" className="block text-sm font-medium text-gray-700 mb-1">Título</label>
                <input type="text" id="itemTitle" name="Title" value={productItemFormState.Title} onChange={handleProductItemFormChange}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  required disabled={isLoading} />
              </div>
              <div>
                <label htmlFor="itemDescription" className="block text-sm font-medium text-gray-700 mb-1">Descripción</label>
                <textarea id="itemDescription" name="Description" value={productItemFormState.Description} onChange={handleProductItemFormChange}
                  rows="2" className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  disabled={isLoading}></textarea>
              </div>
              <div>
                <label htmlFor="itemVideoUrl" className="block text-sm font-medium text-gray-700 mb-1">URL Video</label>
                <input type="text" id="itemVideoUrl" name="VideoUrl" value={productItemFormState.VideoUrl} onChange={handleProductItemFormChange}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  disabled={isLoading} />
              </div>
              <div>
                <label htmlFor="itemPdfUrl" className="block text-sm font-medium text-gray-700 mb-1">URL PDF</label>
                <input type="text" id="itemPdfUrl" name="PdfUrl" value={productItemFormState.PdfUrl} onChange={handleProductItemFormChange}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  disabled={isLoading} />
              </div>
              <div>
                <label htmlFor="itemImageUrl" className="block text-sm font-medium text-gray-700 mb-1">URL Imagen</label>
                <input type="text" id="itemImageUrl" name="ImageUrl" value={productItemFormState.ImageUrl} onChange={handleProductItemFormChange}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  disabled={isLoading} />
              </div>
              <div>
                <label htmlFor="itemDocUrl" className="block text-sm font-medium text-gray-700 mb-1">URL Documento</label>
                <input type="text" id="itemDocUrl" name="DocUrl" value={productItemFormState.DocUrl} onChange={handleProductItemFormChange}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  disabled={isLoading} />
              </div>

              <div className="flex justify-end space-x-4 mt-6">
                <button type="submit"
                  className="inline-flex justify-center py-2 px-6 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  disabled={isLoading}>
                  {isLoading ? (editingProductItem ? 'Actualizando...' : 'Creando...') : (editingProductItem ? 'Actualizar Ítem' : 'Crear Ítem')}
                </button>
                {editingProductItem && (
                  <button type="button" onClick={handleCancelEditProductItem}
                    className="inline-flex justify-center py-2 px-6 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    disabled={isLoading}>
                    Cancelar Edición
                  </button>
                )}
              </div>
            </form>
          </div>

          {/* Lista de Ítems del Producto */}
          <div className="mt-8">
            <h3 className="2xl font-bold text-gray-800 mb-4 text-center">Lista de Ítems</h3>
            {isLoading && <p className="text-center text-gray-600">Cargando ítems...</p>}
            {!isLoading && productItems.length === 0 && <p className="text-center text-gray-600">No se encontraron ítems para este producto.</p>}
            {!isLoading && productItems.length > 0 && (
              <ul className="divide-y divide-gray-200">
                {productItems.map((item) => (
                  <li key={item.Id} className="py-4 flex flex-col md:flex-row justify-between items-start md:items-center">
                    <div className="flex-1 mb-3 md:mb-0 md:mr-6">
                      <h4 className="text-md font-semibold text-gray-900">{item.Title} (<span className="text-indigo-600">{item.Code}</span>)</h4>
                      <p className="text-xs text-gray-600 break-words">{item.PathUrl}</p>
                      {item.Description && <p className="mt-1 text-xs text-gray-500">{item.Description}</p>}
                      {item.VideoUrl && <p className="mt-1 text-xs text-blue-500">Video: <a href={item.VideoUrl} target="_blank" rel="noopener noreferrer" className="hover:underline">Ver</a></p>}
                    </div>
                    <div className="flex flex-wrap gap-2 justify-start md:justify-end">
                      <button onClick={() => handleEditProductItemClick(item)}
                        className="px-3 py-1 bg-yellow-500 text-white text-xs font-medium rounded-md shadow-sm hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 disabled:opacity-50"
                        disabled={isLoading}>
                        Editar Ítem
                      </button>
                      <button onClick={() => handleDeleteProductItemClick(item.Id)}
                        className="px-3 py-1 bg-red-600 text-white text-xs font-medium rounded-md shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                        disabled={isLoading}>
                        Eliminar Ítem
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {/* --- SECCIÓN DE GESTIÓN DE ASIGNACIONES DE USUARIOS --- */}
      <div className="bg-white shadow-xl rounded-xl p-8 border border-gray-200">
        <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center">Gestión de Asignaciones de Usuarios</h2>

        {/* Lista de Usuarios */}
        <div className="bg-gray-50 p-6 rounded-lg shadow-inner mb-8 border border-gray-100">
          <h3 className="text-2xl font-bold text-gray-800 mb-4 text-center">Lista de Usuarios</h3>
          {isLoading && <p className="text-center text-gray-600">Cargando usuarios...</p>}
          {!isLoading && users.length === 0 && <p className="text-center text-gray-600">No se encontraron usuarios.</p>}
          {!isLoading && users.length > 0 && (
            <ul className="divide-y divide-gray-200">
              {users.map((user) => (
                <li key={user.Id} className="py-3 flex flex-col sm:flex-row justify-between items-start sm:items-center">
                  <div className="flex-1 mb-2 sm:mb-0 sm:mr-4">
                    <h4 className="text-lg font-semibold text-gray-900">{user.Username} (<span className="text-indigo-600">{user.Email}</span>)</h4>
                  </div>
                  <button onClick={() => handleManageUserAssignmentsClick(user)}
                    className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                    disabled={isLoading}>
                    Gestionar Asignaciones
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Detalles de Asignaciones del Usuario Seleccionado (condicional) */}
        {selectedUser && (
          <div className="bg-white p-6 rounded-lg shadow-inner border border-gray-100 mt-8">
            <h3 className="2xl font-bold text-gray-800 mb-4 text-center">
              Asignaciones para: <span className="text-blue-600">{selectedUser.Username}</span>
            </h3>
            <div className="flex justify-center mb-6">
              <button onClick={() => setSelectedUser(null)}
                className="inline-flex justify-center py-2 px-6 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-gray-600 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50"
                disabled={isLoading}>
                Cerrar Asignaciones
              </button>
            </div>

            {/* Asignación de Productos al Usuario */}
            <div className="bg-gray-50 p-6 rounded-lg shadow-inner mb-8 border border-gray-100">
              <h4 className="text-xl font-semibold text-gray-800 mb-4">Asignar Productos</h4>
              <div className="flex items-center space-x-4 mb-4">
                <select className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  onChange={(e) => {
                    const productId = parseInt(e.target.value);
                    if (productId) handleAssignProductToUser(selectedUser.Id, productId);
                    e.target.value = ''; // Limpiar select
                  }}
                  disabled={isLoading || availableProductsForUser.length === 0}>
                  <option value="">Seleccionar Producto a Asignar</option>
                  {availableProductsForUser.map(product => (
                    <option key={product.Id} value={product.Id}>
                      {product.Title} ({product.Code}) - Tipo: {product.Type} {/* Mostrar Tipo en el selector */}
                    </option>
                  ))}
                </select>
              </div>

              <h4 className="text-xl font-semibold text-gray-800 mb-4 mt-6">Productos Asignados</h4>
              {isLoading && <p>Cargando productos asignados...</p>}
              {!isLoading && userAssignedProducts.length === 0 && <p>Ningún producto asignado a este usuario.</p>}
              {!isLoading && userAssignedProducts.length > 0 && (
                <ul className="divide-y divide-gray-200">
                  {userAssignedProducts.map(ap => (
                    <li key={ap.UserProductId} className="py-2 flex justify-between items-center">
                      <span>{ap.ProductTitle} (<span className="text-blue-600">{ap.ProductCode}</span>) - Tipo: <span className="font-medium">{ap.ProductType}</span></span> {/* Mostrar Tipo de Producto */}
                      <button onClick={() => handleUnassignProductFromUser(ap.UserProductId, selectedUser.Id)}
                        className="px-3 py-1 bg-red-500 text-white text-xs rounded-md shadow-sm hover:bg-red-600 disabled:opacity-50"
                        disabled={isLoading}>
                        Desasignar
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Asignación de Ítems de Producto al Usuario */}
            <div className="bg-gray-50 p-6 rounded-lg shadow-inner border border-gray-100">
              <h4 className="text-xl font-semibold text-gray-800 mb-4">Asignar Ítems de Producto</h4>
              <div className="flex items-center space-x-4 mb-4">
                <select className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  onChange={(e) => {
                    const productItemId = parseInt(e.target.value);
                    if (productItemId) handleAssignProductItemToUser(selectedUser.Id, productItemId);
                    e.target.value = ''; // Limpiar select
                  }}
                  disabled={isLoading || availableProductItemsForUser.length === 0}>
                  <option value="">Seleccionar Ítem a Asignar</option>
                  {availableProductItemsForUser.map(item => (
                    <option key={item.Id} value={item.Id}>
                      {item.Title} (de Prod: {products.find(p => p.Id === item.ProductId)?.Title || 'N/A'})
                    </option>
                  ))}
                </select>
              </div>

              <h4 className="text-xl font-semibold text-gray-800 mb-4 mt-6">Ítems Asignados</h4>
              {isLoading && <p>Cargando ítems asignados...</p>}
              {!isLoading && userAssignedProductItems.length === 0 && <p>Ningún ítem asignado a este usuario.</p>}
              {!isLoading && userAssignedProductItems.length > 0 && (
                <ul className="divide-y divide-gray-200">
                  {userAssignedProductItems.map(api => (
                    <li key={api.UserProductItemId} className="py-2 flex justify-between items-center">
                      <span>{api.ProductItemTitle} (<span className="text-indigo-600">{api.ProductItemCode}</span>)</span>
                      <button onClick={() => handleUnassignProductItemFromUser(api.UserProductItemId, selectedUser.Id)}
                        className="px-3 py-1 bg-red-500 text-white text-xs rounded-md shadow-sm hover:bg-red-600 disabled:opacity-50"
                        disabled={isLoading}>
                        Desasignar
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductManagement;